import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Columns3, RotateCcw, X } from 'lucide-react'

const cleanLabel = value => value.replace(/\s+/g, ' ').trim() || 'Sem título'
const signature = columns => columns.map(column => column.label).join('|').toLocaleLowerCase('pt-BR')

export default function TableColumnManager({ pathname, userId }) {
  const [tables, setTables] = useState([])
  const [open, setOpen] = useState(false)
  const [portalTarget, setPortalTarget] = useState(null)
  const applying = useRef(false)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)

  const storagePrefix = useMemo(() => `aden:table-columns:${userId || 'anonymous'}:${pathname}`, [pathname, userId])

  useEffect(() => {
    const main = document.getElementById('conteudo-principal')
    if (!main) return undefined
    const scan = () => {
      if (applying.current) return
      const next = [...main.querySelectorAll('table')].map((table, tableIndex) => {
        const headers = [...table.querySelectorAll('thead th')]
        const columns = headers.map((header, index) => ({
          index,
          label: cleanLabel(header.textContent || header.getAttribute('aria-label') || `Coluna ${index + 1}`),
          ignore: header.getAttribute('data-column-ignore') === 'true',
        }))
        const manageableColumns = columns.filter(c => !c.ignore)
        const key = `${storagePrefix}:${tableIndex}:${signature(manageableColumns)}`
        let hidden = []
        try {
          const raw = localStorage.getItem(key)
          if (raw !== null) {
            hidden = JSON.parse(raw)
          } else {
            hidden = manageableColumns
              .filter(column => headers[column.index]?.getAttribute('data-default-hidden') === 'true')
              .map(column => column.index)
          }
        } catch {
          hidden = manageableColumns
            .filter(column => headers[column.index]?.getAttribute('data-default-hidden') === 'true')
            .map(column => column.index)
        }
        applying.current = true
        headers.forEach((header, index) => {
          if (header.getAttribute('data-column-ignore') !== 'true') {
            header.hidden = hidden.includes(index)
          }
        })
        columns.forEach(column => {
          if (!column.ignore) {
            const shouldHide = hidden.includes(column.index)
            table.querySelectorAll(`tbody tr > :nth-child(${column.index + 1})`).forEach(cell => {
              cell.hidden = shouldHide
            })
          }
        })
        applying.current = false
        return {
          table,
          key,
          columns: manageableColumns,
          hidden,
          label: table.querySelector('caption')?.textContent?.trim() || `Lista ${tableIndex + 1}`,
        }
      }).filter(item => item.columns.length > 1)
      setTables(next)
      const firstTable = next[0]?.table
      if (firstTable) {
        const panel = firstTable.closest('.board-table-panel,.today-priority-panel')
        const page = firstTable.closest('.app-main-inner')
        let mount = panel?.querySelector('[data-column-manager-mount]') || page?.querySelector('[data-column-manager-mount]')
        if (!mount) {
          const preferred = panel?.querySelector(':scope > header,:scope > h2') || page?.querySelector('.collection-toolbar,.tickets-toolbar,.tickets-apple-toolbar,.filters-bar,[class*="-filters"]:not([class*="-picker"]):not([class*="-select"]):not([class*="-sort"]):not(button),[class*="-toolbar"]:not([class*="-picker"]):not([class*="-select"]):not([class*="-sort"]):not(button)')
          mount = (preferred || firstTable.parentElement)?.querySelector('[data-column-manager-mount]')
          if (!mount) {
            mount = document.createElement('div'); mount.dataset.columnManagerMount = 'true'; mount.className = 'column-manager-context'
            if (preferred) preferred.appendChild(mount)
            else firstTable.parentElement?.insertBefore(mount, firstTable)
          }
        }
        setPortalTarget(mount)
      }
      if (!next.length) setOpen(false)
    }
    scan()
    const observer = new MutationObserver(scan)
    observer.observe(main, { childList:true, subtree:true })
    return () => observer.disconnect()
  }, [storagePrefix])

  useEffect(() => {
    if (!open) return undefined
    const closeOnOutside = event => {
      if (triggerRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const closeOnEscape = event => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const toggle = (tableIndex, columnIndex) => {
    const current = tables[tableIndex]
    if (!current) return
    const hidden = current.hidden.includes(columnIndex) ? current.hidden.filter(index => index !== columnIndex) : [...current.hidden, columnIndex]
    if (hidden.length >= current.columns.length) return
    localStorage.setItem(current.key, JSON.stringify(hidden))
    const next = tables.map((item, index) => index === tableIndex ? { ...item, hidden } : item)
    setTables(next)
    applying.current = true
    const headers = [...current.table.querySelectorAll('thead th')]
    headers.forEach((header, index) => {
      if (header.getAttribute('data-column-ignore') !== 'true') {
        header.hidden = hidden.includes(index)
      }
    })
    current.columns.forEach(column => {
      const shouldHide = hidden.includes(column.index)
      current.table.querySelectorAll(`tbody tr > :nth-child(${column.index + 1})`).forEach(cell => {
        cell.hidden = shouldHide
      })
    })
    applying.current = false
  }

  const reset = tableIndex => {
    const current = tables[tableIndex]
    if (!current) return
    localStorage.removeItem(current.key)
    const headers = [...current.table.querySelectorAll('thead th')]
    const defaultHidden = current.columns
      .filter(column => headers[column.index]?.getAttribute('data-default-hidden') === 'true')
      .map(column => column.index)
    applying.current = true
    headers.forEach((header, index) => {
      if (header.getAttribute('data-column-ignore') !== 'true') {
        header.hidden = defaultHidden.includes(index)
      }
    })
    current.columns.forEach(column => {
      const shouldHide = defaultHidden.includes(column.index)
      current.table.querySelectorAll(`tbody tr > :nth-child(${column.index + 1})`).forEach(cell => {
        cell.hidden = shouldHide
      })
    })
    applying.current = false
    setTables(items => items.map((item, index) => index === tableIndex ? { ...item, hidden: defaultHidden } : item))
  }

  if (!tables.length || !portalTarget) return null
  return createPortal(
    <div className="column-manager">
      <button ref={triggerRef} type="button" className="column-manager-trigger" onClick={() => setOpen(value => !value)} aria-label="Escolher colunas exibidas" title="Colunas" aria-expanded={open}><Columns3 size={18}/></button>
      {open && (
        <section ref={panelRef} className="column-manager-panel" role="dialog" aria-label="Colunas exibidas">
          <header><strong>Colunas exibidas</strong><button type="button" onClick={() => setOpen(false)} aria-label="Fechar"><X size={16}/></button></header>
          {tables.map((table, tableIndex) => (
            <fieldset key={table.key} aria-label={table.label}>
              {table.columns.map(column => (
                <label key={`${column.index}-${column.label}`}>
                  <input type="checkbox" checked={!table.hidden.includes(column.index)} onChange={() => toggle(tableIndex, column.index)}/>
                  <span>{column.label}</span>
                </label>
              ))}
              <button type="button" className="column-manager-reset" onClick={() => reset(tableIndex)}>
                <RotateCcw size={13}/>Restaurar padrão
              </button>
            </fieldset>
          ))}
        </section>
      )}
    </div>,
    portalTarget
  )
}
